import { useState, useEffect, useMemo } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { Link } from 'react-router-dom';
import { getTasks, getProjects } from '../utils/api';
import { parseLocalDate, fmtDuration, isOverdue } from '../utils/dates';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: (date) => startOfWeek(date, { weekStartsOn: 0 }),
  getDay,
  locales: { 'en-US': enUS },
});

const EVENT_COLORS = {
  task: { bg: '#6366f1', text: '#fff' },
  'task-overdue': { bg: '#ef4444', text: '#fff' },
  project: { bg: '#10b981', text: '#fff' },
};

const CalendarView = () => {
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState('month');
  const [date, setDate] = useState(new Date());

  useEffect(() => {
    Promise.all([getTasks(), getProjects()])
      .then(([t, p]) => {
        setTasks(t.data);
        setProjects(p.data);
      })
      .finally(() => setLoading(false));
  }, []);

  const events = useMemo(() => {
    const taskEvents = tasks
      .filter((t) => t.nextDue)
      .map((t) => {
        const due = parseLocalDate(t.nextDue);
        const overdue = isOverdue(due);
        const duration = fmtDuration(t.estimatedMinutes);
        return {
          id: `task-${t._id}`,
          title: duration ? `${t.title} (${duration})` : t.title,
          start: due,
          end: due,
          allDay: true,
          resource: { type: 'task', overdue, item: t },
        };
      });

    const projectEvents = projects
      .filter((p) => p.targetDate && p.status !== 'completed')
      .map((p) => {
        const target = parseLocalDate(p.targetDate);
        return {
          id: `project-${p._id}`,
          title: `📋 ${p.title}`,
          start: target,
          end: target,
          allDay: true,
          resource: { type: 'project', item: p },
        };
      });

    return [...taskEvents, ...projectEvents];
  }, [tasks, projects]);

  const eventStyleGetter = (event) => {
    const { type, overdue } = event.resource;
    const colorKey = type === 'task' && overdue ? 'task-overdue' : type;
    const colors = EVENT_COLORS[colorKey] || EVENT_COLORS.task;
    return {
      style: {
        backgroundColor: colors.bg,
        color: colors.text,
        border: 'none',
        borderRadius: '4px',
        fontSize: '0.75rem',
        padding: '1px 4px',
      },
    };
  };

  if (loading) return <div className="loading">Loading…</div>;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Calendar</h1>
          <p className="page-subtitle">Upcoming tasks and project target dates</p>
        </div>
      </div>

      {/* Legend */}
      <div className="cal-legend">
        <span className="cal-legend-item"><span className="cal-dot" style={{ background: '#6366f1' }} />Task due</span>
        <span className="cal-legend-item"><span className="cal-dot" style={{ background: '#ef4444' }} />Overdue</span>
        <span className="cal-legend-item"><span className="cal-dot" style={{ background: '#10b981' }} />Project target</span>
      </div>

      <div className="cal-container">
        <Calendar
          localizer={localizer}
          events={events}
          view={view}
          date={date}
          onView={setView}
          onNavigate={setDate}
          eventPropGetter={eventStyleGetter}
          onSelectEvent={(ev) => setSelected(ev)}
          popup
          style={{ height: 600 }}
        />
      </div>

      {/* Event detail panel */}
      {selected && (
        <div className="cal-event-panel">
          <div className="cal-event-panel-header">
            <h3>{selected.resource.type === 'project' ? 'Project' : 'Task'}</h3>
            <button className="btn btn-secondary btn-sm" onClick={() => setSelected(null)}>Close</button>
          </div>
          {selected.resource.type === 'task' ? (
            <TaskDetail task={selected.resource.item} />
          ) : (
            <ProjectDetail project={selected.resource.item} />
          )}
        </div>
      )}
    </div>
  );
};

const TaskDetail = ({ task }) => (
  <div className="cal-event-detail">
    <h4>{task.title}</h4>
    <p className="cal-event-meta">
      <span className={`badge badge-${task.category}`}>{task.category}</span>
      <span className="badge">{task.frequency}</span>
      {task.isOverdue && <span className="badge badge-danger">Overdue</span>}
    </p>
    <p className="cal-event-date">Due: {new Date(task.nextDue).toLocaleDateString()}</p>
    {task.description && <p className="cal-event-desc">{task.description}</p>}
    <Link to="/tasks" className="btn btn-primary btn-sm" style={{ marginTop: '0.5rem' }}>View Tasks</Link>
  </div>
);

const ProjectDetail = ({ project }) => (
  <div className="cal-event-detail">
    <h4>{project.title}</h4>
    <p className="cal-event-meta">
      <span className={`badge badge-${project.priority}`}>{project.priority}</span>
      <span className="badge">{project.status.replace('-', ' ')}</span>
      <span className="badge">{project.category}</span>
    </p>
    <p className="cal-event-date">Target: {new Date(project.targetDate).toLocaleDateString()}</p>
    {project.estimatedCost != null && (
      <p className="cal-event-date">Est. cost: ${Number(project.estimatedCost).toLocaleString()}</p>
    )}
    {project.description && <p className="cal-event-desc">{project.description}</p>}
    <Link to="/projects" className="btn btn-primary btn-sm" style={{ marginTop: '0.5rem' }}>View Projects</Link>
  </div>
);

export default CalendarView;
